import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';

import { useSupabase } from '@kit/supabase/hooks/use-supabase';

import { Notification } from '../types';
import { useNotificationsStream } from './use-notifications-stream';

export function useFetchNotifications({
  onNotifications,
  accountIds,
  realtime,
  enabled = true,
}: {
  onNotifications: (notifications: Notification[]) => unknown;
  accountIds: string[];
  realtime: boolean;
  enabled?: boolean;
}) {
  const { data: initialNotifications } = useFetchInitialNotifications({
    accountIds,
    enabled,
  });

  useNotificationsStream({
    onNotifications,
    accountIds,
    enabled: realtime && enabled,
  });

  useEffect(() => {
    if (initialNotifications && enabled) {
      onNotifications(initialNotifications);
    }
  }, [initialNotifications, onNotifications, enabled]);
}

function useFetchInitialNotifications(props: {
  accountIds: string[];
  enabled?: boolean;
}) {
  const client = useSupabase();
  const now = new Date().toISOString();

  return useQuery<Notification[]>({
    queryKey: ['notifications', ...props.accountIds],
    enabled: props.enabled,
    queryFn: async (): Promise<Notification[]> => {
      const { data, error } = await client
        .from('notifications')
        .select(
          `id,
           body,
           dismissed,
           type,
           created_at,
           link
           `,
        )
        .in('account_id', props.accountIds)
        .gt('expires_at', now)
        .order('created_at', { ascending: false })
        .limit(20);

      if (error) {
        throw error;
      }

      // The select projects exactly the columns of the domain Notification type,
      // so the inferred row shape maps 1:1 without an unsafe cast.
      return (data ?? []).map(
        (row): Notification => ({
          id: row.id,
          body: row.body,
          dismissed: row.dismissed,
          type: row.type,
          created_at: row.created_at,
          link: row.link,
        }),
      );
    },
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
}
